/**
 * CrmProposalsKanban - Kanban de Propostas Geradas (colunas fixas por situação)
 *
 * Fase 1 do módulo "Propostas". Reaproveita integralmente os padrões já
 * consolidados no Kanban de Contratos/Negócios:
 *   - filtros rápidos na barra + filtros avançados em Sheet lateral;
 *   - carregamento por coluna com cursor (sem carregar a base inteira);
 *   - Realtime centralizado (cards mudam de coluna sozinhos);
 *   - modais existentes de Proposta e Negócio ao clicar no card.
 *
 * Permissões: página protegida por `crm_proposals_kanban` (perfil de permissão).
 *
 * @version 1.2.0
 * RELEASE: 2026-08-25 v1.2.0 - Alternador Lista/Kanban ativo: botão "Lista" navega para a
 *          listagem de propostas geradas (/tenant/crm/proposals — Fase 2)
 * RELEASE: 2026-08-25 v1.1.0 - Paridade visual com o Kanban de Contratos: filtros rápidos na
 *          mesma linha do título (sem barra própria e sem linhas horizontais extras), alternador
 *          Kanban/Lista (Lista desabilitada até a Fase 2) e wrapper do board com altura contida
 * RELEASE: 2026-08-25 v1.0.0 - Criação inicial (Fase 1 — Kanban de Propostas)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, LayoutGrid, List, RefreshCw, Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { CrmWorkspaceFilter } from '@/components/crm/filters/CrmWorkspaceFilter';
import { CrmSellerFilter } from '@/components/crm/filters/CrmSellerFilter';
import { CrmUserFilter } from '@/components/crm/filters/CrmUserFilter';
import { CrmPeriodFilter } from '@/components/crm/filters/CrmPeriodFilter';
import type { PeriodFilter, PeriodPreset } from '@/hooks/useCrmOverviewFilters';
import { useCrmProposalTypes } from '@/hooks/useCrmProposalTypes';
import { ProposalKanbanBoard } from '@/components/crm/proposals/kanban/ProposalKanbanBoard';
import { ProposalDetailsModal } from '@/components/crm/deals/modal/tabs/proposals/ProposalDetailsModal';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { useInvalidateProposalKanban, type ProposalFilters } from '@/hooks/useProposalKanbanColumn';
import { startOfMonth, endOfMonth, subDays, startOfDay, endOfDay, subMonths } from 'date-fns';

/** Campos de data disponíveis para o filtro de período */
const DATE_FIELDS: { value: string; label: string }[] = [
  { value: 'created_at', label: 'Criada em' },
  { value: 'sent_at', label: 'Enviada em' },
  { value: 'accepted_at', label: 'Aceita em' },
  { value: 'expires_at', label: 'Expira em' },
  { value: 'updated_at', label: 'Atualizada em' },
];

/** Calcula o intervalo de datas de um preset (mesma semântica dos demais módulos) */
function resolvePreset(preset: PeriodPreset): { startDate: Date; endDate: Date } {
  const now = new Date();
  switch (preset) {
    case 'today':
      return { startDate: startOfDay(now), endDate: endOfDay(now) };
    case 'yesterday': {
      const y = subDays(now, 1);
      return { startDate: startOfDay(y), endDate: endOfDay(y) };
    }
    case '7days':
      return { startDate: startOfDay(subDays(now, 7)), endDate: endOfDay(now) };
    case '15days':
      return { startDate: startOfDay(subDays(now, 15)), endDate: endOfDay(now) };
    case 'lastMonth': {
      const last = subMonths(now, 1);
      return { startDate: startOfMonth(last), endDate: endOfMonth(last) };
    }
    default:
      return { startDate: startOfMonth(now), endDate: endOfMonth(now) };
  }
}

export default function CrmProposalsKanban() {
  // Navegação para a listagem (alternador Lista/Kanban)
  const navigate = useNavigate();

  // ---------------------------------------------------------------- Filtros
  const [search, setSearch] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  // Debounce local da busca (evita uma RPC por tecla digitada)
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [sellerId, setSellerId] = useState<string | null>(null);
  const [proposalTypeId, setProposalTypeId] = useState<string>('all');
  const [dateField, setDateField] = useState<string>('all');
  const [period, setPeriod] = useState<PeriodFilter>({
    preset: 'month',
    ...resolvePreset('month'),
  } as PeriodFilter);

  // Filtros avançados (Sheet)
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [createdBy, setCreatedBy] = useState<string | null>(null);
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [pdfStatus, setPdfStatus] = useState<string>('all');
  const [publicEnabled, setPublicEnabled] = useState<string>('all');
  const [viewed, setViewed] = useState<string>('all');
  const [minTotal, setMinTotal] = useState('');
  const [maxTotal, setMaxTotal] = useState('');
  const [minMrr, setMinMrr] = useState('');
  const [maxMrr, setMaxMrr] = useState('');

  const { activeTypes } = useCrmProposalTypes();
  const invalidateKanban = useInvalidateProposalKanban();

  // Modais
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [dealId, setDealId] = useState<string | null>(null);

  /** Filtros consolidados enviados às RPCs */
  const filters: ProposalFilters = useMemo(
    () => ({
      search: debouncedSearch || null,
      workspace_id: workspaceId,
      seller_id: sellerId,
      proposal_type_id: proposalTypeId === 'all' ? null : proposalTypeId,
      created_by: createdBy,
      assigned_to: assignedTo,
      date_field: dateField === 'all' ? null : dateField,
      date_from: dateField === 'all' ? null : period.startDate.toISOString(),
      date_to: dateField === 'all' ? null : period.endDate.toISOString(),
      pdf_status: pdfStatus === 'all' ? null : pdfStatus,
      public_enabled: publicEnabled === 'all' ? null : publicEnabled === 'yes',
      viewed: viewed === 'all' ? null : viewed === 'yes',
      min_total: minTotal ? Number(minTotal) : null,
      max_total: maxTotal ? Number(maxTotal) : null,
      min_mrr: minMrr ? Number(minMrr) : null,
      max_mrr: maxMrr ? Number(maxMrr) : null,
    }),
    [
      debouncedSearch,
      workspaceId,
      sellerId,
      proposalTypeId,
      createdBy,
      assignedTo,
      dateField,
      period,
      pdfStatus,
      publicEnabled,
      viewed,
      minTotal,
      maxTotal,
      minMrr,
      maxMrr,
    ],
  );

  /** Quantidade de filtros avançados ativos (badge do botão "Mais") */
  const extraFiltersCount = [
    createdBy,
    assignedTo,
    pdfStatus !== 'all' ? pdfStatus : null,
    publicEnabled !== 'all' ? publicEnabled : null,
    viewed !== 'all' ? viewed : null,
    minTotal || null,
    maxTotal || null,
    minMrr || null,
    maxMrr || null,
  ].filter(Boolean).length;

  const hasActiveFilters =
    !!search ||
    !!workspaceId ||
    !!sellerId ||
    proposalTypeId !== 'all' ||
    dateField !== 'all' ||
    extraFiltersCount > 0;

  /** Limpa todos os filtros de uma vez */
  const clearFilters = () => {
    setSearch('');
    setWorkspaceId(null);
    setSellerId(null);
    setProposalTypeId('all');
    setDateField('all');
    setCreatedBy(null);
    setAssignedTo(null);
    setPdfStatus('all');
    setPublicEnabled('all');
    setViewed('all');
    setMinTotal('');
    setMaxTotal('');
    setMinMrr('');
    setMaxMrr('');
  };

  const triggerClass = 'h-[22px] px-2 text-xs shrink-0 max-w-[150px] border-0 bg-background rounded-full';

  return (
    <div className="flex flex-col h-full">
      {/* Cabeçalho compacto — v1.1.0: título + filtros rápidos na MESMA linha
          (mesmo padrão do Kanban de Contratos), sem barra de filtros separada
          e sem linhas horizontais extras. */}
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1 min-w-0 sm:shrink-0">
          <h1 className="text-xl font-semibold shrink-0">Kanban de Propostas</h1>

          {/* Busca expansível (sanfona) — mesmo padrão do Kanban de Negócios */}
          <div className="hidden md:flex items-center">
            <div
              className={cn(
                'overflow-hidden transition-all duration-300 ease-in-out',
                isSearchExpanded ? 'w-44 opacity-100 ml-2 mr-1' : 'w-0 opacity-0',
              )}
            >
              <div className="relative w-full">
                <Input
                  placeholder="Buscar proposta, cliente, negócio..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onBlur={() => { if (!search) setIsSearchExpanded(false); }}
                  className="h-7 w-full text-xs rounded-full pl-3 pr-7"
                  autoFocus={isSearchExpanded}
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-full shrink-0"
              onClick={() => setIsSearchExpanded((prev) => (prev && !search ? false : !prev))}
              title="Buscar propostas"
            >
              {isSearchExpanded ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Faixa de filtros rápidos — rola horizontalmente no mobile */}
        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide min-w-0 sm:flex-1">
          <Separator orientation="vertical" className="h-5 hidden sm:block shrink-0" />

          {/* Busca compacta apenas no mobile (no desktop existe a lupa expansível) */}
          <div className="relative md:hidden shrink-0 w-40">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-[22px] w-full pl-7 text-xs rounded-full"
            />
          </div>

          <CrmWorkspaceFilter value={workspaceId} onChange={setWorkspaceId} className={triggerClass} />

          <CrmSellerFilter value={sellerId} onChange={setSellerId} className={triggerClass} />

          {/* Tipo da proposta */}
          <Select value={proposalTypeId} onValueChange={setProposalTypeId}>
            <SelectTrigger
              className={cn(triggerClass, proposalTypeId !== 'all' && 'border-primary bg-primary/5')}
            >
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              {activeTypes.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Campo de data + período */}
          <Select value={dateField} onValueChange={setDateField}>
            <SelectTrigger
              className={cn(triggerClass, dateField !== 'all' && 'border-primary bg-primary/5')}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <Calendar className="h-3 w-3" />
                <span className="truncate">
                  {dateField === 'all'
                    ? 'Período'
                    : DATE_FIELDS.find((d) => d.value === dateField)?.label}
                </span>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas datas</SelectItem>
              {DATE_FIELDS.map((d) => (
                <SelectItem key={d.value} value={d.value}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {dateField !== 'all' && (
            <CrmPeriodFilter
              value={period}
              onPresetChange={(preset) => setPeriod({ preset, ...resolvePreset(preset) } as PeriodFilter)}
              onCustomChange={(startDate, endDate) =>
                setPeriod({ preset: 'custom', startDate, endDate } as PeriodFilter)
              }
              className="h-[22px] px-2 text-xs shrink-0"
            />
          )}

          {/* Filtros avançados */}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 shrink-0 h-[22px] px-2 text-xs"
            onClick={() => setIsMoreOpen(true)}
          >
            <SlidersHorizontal className="h-3 w-3" />
            Mais
            {extraFiltersCount > 0 && (
              <Badge
                variant="secondary"
                className="h-4 min-w-[16px] px-1 text-[10px] rounded-full bg-primary/15 text-primary border-0"
              >
                {extraFiltersCount}
              </Badge>
            )}
          </Button>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground hover:text-foreground shrink-0 h-[22px] px-2 text-xs"
            >
              <X className="h-3 w-3 mr-1" />
              Limpar
            </Button>
          )}
        </div>

        {/* Ações à direita — alternador Kanban/Lista + atualizar */}
        <div className="flex items-center gap-2 shrink-0">
          {/* v1.2.0: alternador no mesmo padrão do Kanban de Contratos.
              A listagem de propostas (Fase 2) já existe em /tenant/crm/proposals. */}
          <div className="flex items-center rounded-md border p-0.5">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => navigate('/tenant/crm/proposals')}
              title="Visualizar em lista"
            >
              <List className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="secondary"
              size="icon-sm"
              title="Visualizar em kanban por situação"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </Button>
          </div>


          <Button variant="outline" size="icon-sm" onClick={invalidateKanban} title="Atualizar dados">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Board — altura contida para que o scroll aconteça dentro de cada coluna */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <ProposalKanbanBoard
          filters={filters}
          onOpenProposal={setDetailsId}
          onOpenDeal={setDealId}
        />
      </div>



      {/* Sheet de filtros avançados */}
      <Sheet open={isMoreOpen} onOpenChange={setIsMoreOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Filtros avançados</SheetTitle>
            <SheetDescription>Refine a busca das propostas geradas</SheetDescription>
          </SheetHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Criada por</Label>
              <CrmUserFilter
                value={createdBy}
                onChange={setCreatedBy}
                label="Criada por"
                searchPlaceholder="Buscar usuário..."
                allLabel="Todos os usuários"
                groupLabel="Usuários"
                className="h-8 text-sm w-full"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Responsável pelo negócio</Label>
              <CrmUserFilter
                value={assignedTo}
                onChange={setAssignedTo}
                label="Responsável"
                searchPlaceholder="Buscar usuário..."
                allLabel="Todos os responsáveis"
                groupLabel="Usuários"
                className="h-8 text-sm w-full"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Situação do PDF</Label>
              <Select value={pdfStatus} onValueChange={setPdfStatus}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="queued">Na fila</SelectItem>
                  <SelectItem value="generated">Gerado</SelectItem>
                  <SelectItem value="error">Erro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Link público</Label>
              <Select value={publicEnabled} onValueChange={setPublicEnabled}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="yes">Com link ativo</SelectItem>
                  <SelectItem value="no">Sem link</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Visualização pelo cliente</Label>
              <Select value={viewed} onValueChange={setViewed}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="yes">Visualizadas</SelectItem>
                  <SelectItem value="no">Não visualizadas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Valor mínimo</Label>
                <Input
                  type="number"
                  value={minTotal}
                  onChange={(e) => setMinTotal(e.target.value)}
                  className="h-8 text-sm"
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Valor máximo</Label>
                <Input
                  type="number"
                  value={maxTotal}
                  onChange={(e) => setMaxTotal(e.target.value)}
                  className="h-8 text-sm"
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">MRR mínimo</Label>
                <Input
                  type="number"
                  value={minMrr}
                  onChange={(e) => setMinMrr(e.target.value)}
                  className="h-8 text-sm"
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">MRR máximo</Label>
                <Input
                  type="number"
                  value={maxMrr}
                  onChange={(e) => setMaxMrr(e.target.value)}
                  className="h-8 text-sm"
                  placeholder="0,00"
                />
              </div>
            </div>

            <Button variant="outline" className="w-full" onClick={clearFilters}>
              <X className="h-3.5 w-3.5 mr-1" />
              Limpar filtros
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal de detalhes da proposta (mesmo usado na aba do negócio) */}
      <ProposalDetailsModal
        proposalId={detailsId}
        onClose={() => setDetailsId(null)}
        /* Atalho do cabeçalho: fecha a proposta e abre o negócio vinculado */
        onOpenDeal={(id) => {
          setDetailsId(null);
          setDealId(id);
        }}
      />

      {/* Modal do negócio vinculado */}
      <DealDetailsModalPremium
        dealId={dealId}
        open={!!dealId}
        onOpenChange={(open) => !open && setDealId(null)}
      />
    </div>
  );
}
